<script setup lang="ts">
import { useCourseExemptionMutations } from '@/composables/mutations/tertiary/useCourseExemptionMutations';
import { useFaculties } from '@/composables/queries/useFaculties';
import { useDepartment } from '@/composables/queries/useDepartment';
import { useProgrammes } from '@/composables/queries/useProgrammes';
import { useCourses } from '@/composables/queries/useCourses';
import { formatCourseInstance } from '@/utils/courseInstance';
import { useTertiaryLevels } from '@/composables/queries/useTertiaryLevels';
import { ExemptionScopeType } from '@/types/composition-functions';

const emit = defineEmits<{ success: [] }>();
const visible = defineModel<boolean>('visible', { default: false });

const { bulkExempt, bulkExempting } = useCourseExemptionMutations();

// Scope data sources
const { faculties } = useFaculties();
const { departments, loadSchoolDepartment } = useDepartment({ autoFetch: false });
const { programmes, loadSchoolProgramme } = useProgrammes({ autoFetch: false, first: 100, page: 1 });
const { courses, setFilter: setCourseFilter } = useCourses({ autoFetch: true, first: 100, page: 1 });

let courseDebounce: ReturnType<typeof setTimeout> | null = null;
function onCourseFilter(event: { originalEvent: Event; value: string }) {
  if (courseDebounce) clearTimeout(courseDebounce);
  courseDebounce = setTimeout(() => {
    setCourseFilter({ query: event.value });
  }, 300);
}

// Load departments and programmes on mount
onMounted(() => {
  loadSchoolDepartment();
  loadSchoolProgramme();
});

// Scope type options
const scopeTypeOptions = [
  { label: 'Programme', value: ExemptionScopeType.Programme },
  { label: 'Department', value: ExemptionScopeType.Department },
  { label: 'Faculty', value: ExemptionScopeType.Faculty },
];

const { levelOptionsAsString: levelOptions } = useTertiaryLevels();

// Form state
const selectedScopeType = ref<ExemptionScopeType | null>(null);
const selectedScopeId = ref<string | null>(null);
const selectedLevels = ref<string[]>([]);
const selectedCourses = ref<string[]>([]);

// Dynamic scope options based on selected type
const scopeIdOptions = computed(() => {
  if (!selectedScopeType.value) return [];
  switch (selectedScopeType.value) {
  case ExemptionScopeType.Faculty:
    return faculties.value.map((f) => ({ label: f.faculty_name, value: f.id }));
  case ExemptionScopeType.Department:
    return (departments.value?.data ?? []).map((d: { id: string; department_name: string }) => ({ label: d.department_name, value: d.id }));
  case ExemptionScopeType.Programme:
    return programmes.value.map((p: { id: string; prog_code: string; program_name: string }) => ({ label: `${p.prog_code} - ${p.program_name}`, value: p.id }));
  default:
    return [];
  }
});

const scopeIdLabel = computed(() => {
  switch (selectedScopeType.value) {
  case ExemptionScopeType.Faculty: return 'Faculty';
  case ExemptionScopeType.Department: return 'Department';
  case ExemptionScopeType.Programme: return 'Programme';
  default: return 'Scope';
  }
});

const courseOptions = computed(() =>
  courses.value.map((c: { id: string; course_code: string; course_title: string; unit: number; course_instance_id?: string; course_instance_year?: number | null }) => ({
    label: `${c.course_code} — ${c.course_title} (${c.unit} units)`,
    value: c.id,
    code: c.course_code,
    title: c.course_title,
    unit: c.unit,
    instance: formatCourseInstance(c),
  })),
);

// Reset scope_id when type changes
watch(selectedScopeType, () => {
  selectedScopeId.value = null;
});

async function handleSubmit() {
  if (!selectedScopeType.value || !selectedScopeId.value || !selectedLevels.value.length || !selectedCourses.value.length) return;
  const result = await bulkExempt({
    scope_type: selectedScopeType.value,
    scope_id: selectedScopeId.value,
    levels: selectedLevels.value,
    course_ids: selectedCourses.value,
  });
  if (result?.success) {
    resetForm();
    visible.value = false;
    emit('success');
  }
}

function resetForm() {
  selectedScopeType.value = null;
  selectedScopeId.value = null;
  selectedLevels.value = [];
  selectedCourses.value = [];
}

function onHide() {
  resetForm();
}
</script>

<template>
  <Dialog data-testid="bulk-scope-dialog" v-model:visible="visible" header="Bulk Exempt by Scope" modal :style="{ width: '36rem' }" @hide="onHide">
    <div class="flex flex-col gap-4 pt-2">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">Scope Type</label>
        <Select
          data-testid="bulk-scope-type-select"
          v-model="selectedScopeType"
          :options="scopeTypeOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Select scope type..."
          class="w-full"
        />
      </div>

      <div v-if="selectedScopeType" class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">{{ scopeIdLabel }}</label>
        <Select
          data-testid="bulk-scope-id-select"
          v-model="selectedScopeId"
          :options="scopeIdOptions"
          optionLabel="label"
          optionValue="value"
          :placeholder="`Select ${scopeIdLabel.toLowerCase()}...`"
          filter
          class="w-full"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">Levels</label>
        <MultiSelect
          data-testid="bulk-scope-levels-select"
          v-model="selectedLevels"
          :options="levelOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Select levels..."
          class="w-full"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">Courses to Exempt</label>
        <MultiSelect
          data-testid="bulk-scope-course-select"
          v-model="selectedCourses"
          :options="courseOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Search courses..."
          filter
          @filter="onCourseFilter"
          :maxSelectedLabels="3"
          class="w-full"
        >
          <template #option="{ option }">
            <div class="flex flex-col py-0.5">
              <span class="text-sm font-medium">{{ option.code }} — {{ option.title }}</span>
              <span class="text-xs text-gray-400">Unit: {{ option.unit ?? '—' }} · {{ option.instance || '—' }}</span>
            </div>
          </template>
        </MultiSelect>
        <small class="text-gray-500">{{ selectedCourses.length }} course(s) selected</small>
      </div>
    </div>

    <template #footer>
      <Button size="small" data-testid="bulk-scope-cancel" label="Cancel" severity="secondary" text @click="visible = false" />
      <Button size="small"
        data-testid="bulk-scope-submit"
        label="Apply Exemptions"
        icon="pi pi-check"
        @click="handleSubmit"
        :loading="bulkExempting"
        :disabled="!selectedScopeType || !selectedScopeId || !selectedLevels.length || !selectedCourses.length"
      />
    </template>
  </Dialog>
</template>
